import { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { AchievementsScreen } from '../screens/Achievements/AchievementsScreen';
import { ChildHomeScreen } from '../screens/ChildHome/ChildHomeScreen';
import { IslandScreen } from '../screens/Island/IslandScreen';
import { SessionScreen } from '../screens/Session/SessionScreen';
import { AnchoredPrompt } from '../components/AnchoredPrompt';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { Strings } from '../strings';

import { ChildRoute } from './routes';

/**
 * design-system/06-Components/06-Navigation-And-Structure.md §1 — bottom navigation, four items, at
 * `--touch-child` sizing.
 * Mirrors android/…/Navigation/ChildShell.kt.
 *
 * There is deliberately no route from here to any parent screen: the shells are exclusive (ADR-0006). The
 * only way out is „Späť k rodičovi", which is a **control rather than a destination** — it ends child mode,
 * so it owns no screen and no route.
 *
 * **The bar is hidden during a task.** A permanent bar under a child's thumb, next to a control that ends
 * child mode, is one mis-tap away from abandoning tasks the server has already issued. Leaving mid-session
 * is still allowed — through the close affordance on the session screen, where it is a decision.
 */
export function ChildShell({
  nickname,
  onLeaveChildMode,
}: {
  nickname: string;
  onLeaveChildMode: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmingExit, setConfirmingExit] = useState(false);
  const isSession = location.pathname === ChildRoute.session;

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-canvas)',
      }}
    >
      <main
        style={{
          flex: 1,
          maxWidth: 'var(--column)',
          margin: '0 auto',
          padding: 'var(--space-5)',
          /*
            Room for the anchored confirmation, which is `position: fixed` and takes none of its own. The
            child pill is sized from `--touch-child`, so the clearance is a touch taller here (R0146).
          */
          paddingBottom:
            confirmingExit && !isSession
              ? 'calc(var(--space-5) + var(--touch-child) + var(--space-4) * 3)'
              : 'var(--space-5)',
          width: '100%',
        }}
      >
        <Routes>
          <Route
            path="home"
            element={
              <ChildHomeScreen
                nickname={nickname}
                onStartTraining={() => navigate(ChildRoute.session)}
                onOpenIsland={() => navigate(ChildRoute.island)}
                onOpenAchievements={() => navigate(ChildRoute.achievements)}
              />
            }
          />
          <Route path="session" element={<SessionScreen onLeave={() => navigate(ChildRoute.home)} />} />
          <Route path="island" element={<IslandScreen />} />
          <Route path="achievements" element={<AchievementsScreen />} />
          <Route path="*" element={<Navigate to={ChildRoute.home} replace />} />
        </Routes>
      </main>

      {/*
        Anchored above the navigation rather than inline — design-system §4. Rendered in the flow it pushed
        „Moje ocenenia" and everything under it down the page, so the answer arrived somewhere other than
        where the question had been (R0099).
      */}
      {confirmingExit && !isSession ? (
        <AnchoredPrompt text={Strings.childSwitchBackConfirm} isChildMode>
          <PrimaryButton
            label={Strings.childSwitchBack}
            onClick={onLeaveChildMode}
            height="var(--touch-child)"
          />
          <SecondaryButton label={Strings.actionStay} onClick={() => setConfirmingExit(false)} />
        </AnchoredPrompt>
      ) : null}

      {isSession ? null : (
        <ChildTabBar
          current={location.pathname}
          onNavigate={(path: string) => {
            setConfirmingExit(false);
            navigate(path);
          }}
          onLeave={() => setConfirmingExit(true)}
        />
      )}
    </div>
  );
}

const CHILD_TABS = [
  { path: ChildRoute.home, label: Strings.childNavHome },
  { path: ChildRoute.island, label: Strings.childNavIsland },
  { path: ChildRoute.achievements, label: Strings.childNavAchievements },
];

/**
 * The parent shell's bar, at child sizing.
 *
 * „Späť k rodičovi" is rendered beside the three destinations and behaves differently: it never becomes
 * the current item, because it is not a place.
 */
function ChildTabBar({
  current,
  onNavigate,
  onLeave,
}: {
  current: string;
  onNavigate: (path: string) => void;
  onLeave: () => void;
}) {
  return (
    <nav
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border-default)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div style={{ display: 'flex', width: '100%', maxWidth: 'var(--column)' }}>
        {CHILD_TABS.map((tab) => (
          <TabButton
            key={tab.path}
            label={tab.label}
            isCurrent={current === tab.path}
            onClick={() => onNavigate(tab.path)}
          />
        ))}
        <TabButton label={Strings.childSwitchBack} isCurrent={false} onClick={onLeave} />
      </div>
    </nav>
  );
}

function TabButton({
  label,
  isCurrent,
  onClick,
}: {
  label: string;
  isCurrent: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={isCurrent ? 'page' : undefined}
      onClick={onClick}
      style={{
        flex: 1,
        minHeight: 'calc(var(--touch-child) + var(--space-4))',
        background: 'none',
        border: 'none',
        color: isCurrent ? 'var(--action-primary)' : 'var(--text-secondary)',
        fontSize: '15px',
        lineHeight: '20px',
        fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}
